import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

import { useHSOverlay } from '@core/hooks/useHSOverlay';

const VARIANT_STYLES = {
  danger: {
    iconBg: 'bg-[var(--color-danger-subtle)] text-[var(--color-danger)]',
    button: 'bg-[var(--color-danger)] text-white hover:bg-red-700 focus:bg-red-700',
  },
  warning: {
    iconBg: 'bg-amber-50 text-amber-600',
    button: 'bg-amber-600 text-white hover:bg-amber-700 focus:bg-amber-700',
  },
};

/**
 * Reusable confirmation modal for destructive actions.
 *
 * Uses Preline UI HSOverlay for animations, escape key, and backdrop click.
 */
export function ConfirmModal({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel,
  variant = 'danger',
}) {
  const { t } = useTranslation();
  const { overlayId, overlayRef } = useHSOverlay(isOpen, onCancel, 'hs-confirm-modal');
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.danger;

  return (
    <div
      id={overlayId}
      ref={overlayRef}
      // whitespace-normal: the modal can be mounted inside inline containers
      // that set `whitespace-nowrap`, which would otherwise be inherited and
      // push the title and message outside the dialog instead of wrapping.
      className="hs-overlay hidden size-full fixed top-0 start-0 z-80 overflow-x-hidden overflow-y-auto pointer-events-none whitespace-normal"
      role="dialog"
      tabIndex={-1}
      aria-labelledby={`${overlayId}-label`}
    >
      <div className="hs-overlay-open:mt-7 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-0 opacity-0 ease-out transition-all sm:max-w-md sm:w-full m-3 sm:mx-auto min-h-[calc(100%-56px)] flex items-center">
        <div className="w-full flex flex-col bg-white border border-[var(--color-border)] shadow-sm rounded-lg pointer-events-auto">
          <div className="p-4 sm:p-7 text-center">
            <div className="flex justify-center mb-4">
              <span className={`inline-flex justify-center items-center size-12 rounded-full ${styles.iconBg}`}>
                <Icon icon="mdi:alert-outline" className="size-6" />
              </span>
            </div>
            <h3 id={`${overlayId}-label`} className="text-lg font-semibold break-words text-[var(--color-text)]">{title}</h3>
            <p className="mt-2 text-sm break-words text-[var(--color-text-secondary)]">{message}</p>
          </div>
          <div className="flex justify-end items-center gap-x-2 py-3 px-4 border-t border-[var(--color-border)]">
            <button
              type="button"
              onClick={onCancel}
              className="cursor-pointer py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text)] shadow-sm hover:bg-[var(--color-bg-muted)] focus:outline-hidden"
            >
              {t('dataroom.confirmModal.cancel')}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`cursor-pointer py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent ${styles.button} focus:outline-hidden`}
            >
              {confirmLabel ?? t('dataroom.confirmModal.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
